import { Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { RESERVED_URL_SEARCH_PARAMS } from "utils/routes.constants";

import { useLoggedInUser } from "hooks/useLoggedInUser";
import { Privilege } from "services/rbac/types";

import ErrorBoundary from "components/ErrorBoundary";
import LoadingOverlap from "components/LoadingOverlap";

import { PrivilegesMatchStrategy, Restricted } from "./Restricted";

const Protected = (props: {
  Component: React.ComponentType;
  privileges: Privilege[];
  matchStrategy?: PrivilegesMatchStrategy;
  bypassOrgAdmin?: boolean;
}) => {
  const { Component, privileges, matchStrategy, bypassOrgAdmin, ...rest } =
    props;
  const location = useLocation();

  const { isLoggedIn } = useLoggedInUser();

  if (isLoggedIn) {
    return (
      <Restricted
        privileges={privileges}
        matchStrategy={matchStrategy}
        bypassOrgAdmin={bypassOrgAdmin}
      >
        <Component {...rest} />
      </Restricted>
    );
  }

  const redirect = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  searchParams.set(RESERVED_URL_SEARCH_PARAMS.REDIRECT, redirect);

  const to = `/login?${searchParams}`;

  return <Navigate to={to} />;
};

export const protectedRoute = (
  Component: React.ComponentType,
  config?: {
    privileges: Privilege[];
    matchStrategy?: PrivilegesMatchStrategy;
    bypassOrgAdmin?: boolean;
  }
) => {
  const { privileges, matchStrategy, bypassOrgAdmin } = config || {
    privileges: [],
    matchStrategy: PrivilegesMatchStrategy.SOME,
    bypassOrgAdmin: false,
  };

  return (props: React.ComponentProps<typeof Component>) => {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingOverlap isLoading />}>
          <Protected
            Component={Component}
            privileges={privileges}
            matchStrategy={matchStrategy}
            bypassOrgAdmin={bypassOrgAdmin}
            {...props}
          />
        </Suspense>
      </ErrorBoundary>
    );
  };
};
